import { useCallback, useEffect } from 'react';
import { QueryResult, useLazyQuery, useReactiveVar } from '@apollo/client';
import customizeCar from '../../graphql/queries/customizeCar';
import { GetCustomizeCar, GetCustomizeCarVariables } from '../../graphql/queries/__generated__/GetCustomizeCar';
import { hasFinanceProviderErrorVar } from '../../../gqlCommon/graphql/cache';
import useGetPostCode from '../../../gqlCommon/hooks/useGetPostCode';
import { IUseFinanceFormInputReturn, useFinanceFormInput } from '../../../gqlCommon/hooks/useFinanceFormInput';
import usePlatformConfig from '../../../common/react/usePlatformConfig';
import useSalesEnablementContext from '../../../gqlCommon/hooks/useSalesEnablementContext';
import { FinanceFormInput, FinanceFormType } from '../../../__generated__/globalTypes';
import useInsuranceFormInput from '../../../gqlCommon/hooks/useInsuranceFormInput';
import useUrlSearchParams from '../useUrlSearchParams';
import useCustomerData from '../../../gqlCommon/hooks/useCustomerData';

const useCustomizeCar = () => {
  const { searchParams } = useUrlSearchParams();
  const carId = searchParams?.carCode;
  const selectedFeatureIds = searchParams?.selectedFeatures as string[];
  const postCode = useGetPostCode();
  const { customizeVersion, isInSalesEnablement } = useSalesEnablementContext();
  const { financeFormInput, financeInput } = useFinanceFormInput();
  const hasFinanceProviderError = useReactiveVar(hasFinanceProviderErrorVar);
  const vehicleConfigurationUrl = window.location.href;
  const {
    customerData: { dealerId },
  } = useCustomerData() || {};

  const {
    platformConfig: { stockInteraction, showRefundableAmount, showIndicativeDeliveryTimeframe, dataLayer },
  } = usePlatformConfig();

  const cashFormInput: FinanceFormInput = {
    paymentType: FinanceFormType.cash,
  };

  const cashInput: IUseFinanceFormInputReturn['financeInput'] = {
    journeyType: cashFormInput.paymentType,
  };

  const [query, { data, loading, error }] = useLazyQuery<GetCustomizeCar, GetCustomizeCarVariables>(
    customizeCar.GET_CUSTOMIZE_CAR,
    {
      fetchPolicy: 'no-cache',
      nextFetchPolicy: 'no-cache',
    },
  );

  const { insuranceFormData } = useInsuranceFormInput(data?.customizeCar?.car.byoCode as string);

  const loadCustomizeCar = useCallback(
    (
      newVariables?: Partial<GetCustomizeCarVariables>,
      callback?: (result?: QueryResult<GetCustomizeCar, GetCustomizeCarVariables>) => void,
    ) => {
      const controller = new window.AbortController();

      query({
        variables: {
          carId,
          postCode,
          dealerId,
          financeProductId: hasFinanceProviderError
            ? cashFormInput.financeProductId
            : financeFormInput?.financeProductId,
          financeInput: hasFinanceProviderError ? cashInput : financeInput,
          financeFormInput: hasFinanceProviderError ? cashFormInput : financeFormInput,
          needsInteraction: stockInteraction,
          vehicleConfigurationUrl: isInSalesEnablement ? vehicleConfigurationUrl : '',
          insuranceFormInput: insuranceFormData ? insuranceFormData : null,
          includeReservationFee: !!showRefundableAmount,
          includeIndicativeDeliveryTimeframe: !!showIndicativeDeliveryTimeframe,
          ...newVariables,
          selectedFeatureIds: {
            selected: selectedFeatureIds,
            ...newVariables?.selectedFeatureIds,
          },
          dataLayer: !!dataLayer,
        },
        context: {
          fetchOptions: {
            signal: controller.signal,
          },
        },
      }).then(result => callback && callback(result));

      return () => controller.abort();
    },
    [
      query,
      carId,
      dealerId,
      selectedFeatureIds,
      postCode,
      hasFinanceProviderError,
      cashFormInput,
      cashInput,
      financeFormInput,
      financeInput,
      stockInteraction,
      isInSalesEnablement,
      insuranceFormData,
      showIndicativeDeliveryTimeframe,
      showRefundableAmount,
      vehicleConfigurationUrl,
    ],
  );

  useEffect(() => {
    if (customizeVersion !== 0) {
      loadCustomizeCar();
    }
  }, [customizeVersion]);

  return {
    data,
    loading,
    error,
    loadCustomizeCar,
  };
};

export default useCustomizeCar;
